import type { locales, pathnames } from "@/config";

type Locale = (typeof locales)[number];

/**
 * Blog posts written only in some locales. Elsewhere the page 404s and stays
 * out of links, the sitemap and the language menu. Kept apart from
 * blog-posts.ts, which imports next-intl/server, so the client-side language
 * menu can read it. Add a locale here when its translation ships.
 */
export const BLOG_POST_LOCALES = {
  "/blog/collect-wedding-photos-from-guests": ["en", "da", "sv", "no", "de", "fr", "es"],
  "/blog/digital-guestbook": ["da", "de"],
  "/blog/wedding-planning-checklist": ["da", "sv", "no"],
  "/blog/quinceanera-photo-sharing": ["en", "es"],
  "/blog/graduation-party-photo-sharing": ["en"],
  "/blog/bridal-shower-photo-sharing": ["en"],
  "/blog/when-guests-upload-photos": ["en", "da"],
  "/blog/konfirmation-checklist": ["da", "sv", "no"],
  "/blog/konfirmation-billedshow": ["da", "no"],
  "/blog/wedding-slideshow": ["sv"],
  "/blog/photo-challenges": ["da"],
} as const satisfies Partial<Record<keyof typeof pathnames, readonly Locale[]>>;

/**
 * The locales the templated posts (photo wall, full-quality uploads, case
 * story, faster uploads) are written in. Elsewhere they render the English
 * article and are noindex. The content files key their text by this list, so
 * adding a locale here fails the type-check until its text exists.
 */
export const TEMPLATE_LOCALES = ["en", "da", "no", "sv", "de"] as const satisfies readonly Locale[];

export type TemplateLocale = (typeof TEMPLATE_LOCALES)[number];

/** The locales a route exists in, or undefined when it exists in all of them. */
export function getRouteLocales(pathname: string): readonly Locale[] | undefined {
  return (BLOG_POST_LOCALES as Partial<Record<string, readonly Locale[]>>)[pathname];
}

/** The language a templated post renders in a locale: its own text, else English. */
export function getTemplateContentLocale(locale: string): TemplateLocale {
  return (TEMPLATE_LOCALES as readonly string[]).includes(locale) ? (locale as TemplateLocale) : "en";
}
